import { NavLink, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { getCurrentUser, logoutUser } from "../utils/auth";
import { disconnectBank, isBankConnected } from "../utils/plaidIntegration";
import { getPremiumStatus } from "../utils/paywall";
import { openCustomerPortal } from "../utils/subscriptionManager";

export default function Header() {
  const [open, setOpen] = useState(false);                     // mobile menu
  const [calcOpen, setCalcOpen] = useState(false);             // desktop: Calculators dropdown
  const [toolsOpen, setToolsOpen] = useState(false);           // desktop: Tools dropdown
  const [resOpen, setResOpen] = useState(false);               // desktop: Blog dropdown
  const [userDropdownOpen, setUserDropdownOpen] = useState(false); // user account dropdown
  const [currentUser, setCurrentUser] = useState(getCurrentUser());

  const calcRef = useRef<HTMLDivElement | null>(null);
  const toolsRef = useRef<HTMLDivElement | null>(null);
  const resRef = useRef<HTMLDivElement | null>(null);
  const userRef = useRef<HTMLDivElement | null>(null);

  // Check for auth changes (e.g., after login/logout)
  useEffect(() => {
    const checkAuth = () => {
      setCurrentUser(getCurrentUser());
    };
    
    // Listen for storage events (cross-tab login/logout)
    window.addEventListener('storage', checkAuth);
    
    // Custom event for same-tab login/logout
    window.addEventListener('auth-change', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('auth-change', checkAuth);
    };
  }, []);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  const handleLogout = () => {
    // Disconnect bank for this user
    disconnectBank(currentUser?.email);
    // Clear auth
    logoutUser();
    // Reset state
    setCurrentUser(null);
    setUserDropdownOpen(false);
    // Redirect to home
    window.location.href = '/';
  };

  const handleDisconnectBank = () => {
    if (confirm('Are you sure you want to disconnect your bank account?')) {
      disconnectBank(currentUser?.email);
      setUserDropdownOpen(false);
    }
  };

  const premiumStatus = currentUser ? getPremiumStatus() : { isPremium: false, stripeCustomerId: null };
  const bankConnected = currentUser ? isBankConnected(currentUser.email) : false;

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    "px-3 py-2 text-sm font-medium rounded-md transition-colors " +
    (isActive ? "text-primary-700 bg-primary-50" : "text-slate-700 hover:text-primary-600 hover:bg-slate-50");

  return (
    <header data-id="app-header" className="sticky top-0 z-40 w-full border-b bg-white shadow-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 group">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary-600 to-primary-700 text-white text-sm font-bold shadow-md group-hover:shadow-lg transition-shadow">f9</span>
          <span className="font-bold text-lg text-slate-900">free1099calc</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {/* Calculators dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setCalcOpen(true)}
            onMouseLeave={() => setCalcOpen(false)}
            ref={calcRef}
          >
            <NavLink to="/calculators" className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-black">
              Calculators
            </NavLink>
            {calcOpen && (
              <div className="absolute left-0 mt-2 w-56 rounded-lg border bg-white shadow-lg">
                <div className="py-1">
                  <NavLink
                    to="/"
                    end
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    Tax Calculator
                  </NavLink>
                  <NavLink
                    to="/retirement"
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    Retirement Calculator
                  </NavLink>
                  <NavLink
                    to="/calculators/inflation"
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    Inflation Calculator
                  </NavLink>
                </div>
              </div>
            )}
          </div>

          {/* Tools dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setToolsOpen(true)}
            onMouseLeave={() => setToolsOpen(false)}
            ref={toolsRef}
          >
            <NavLink to="/tools" className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-black">
              Tools
            </NavLink>
            {toolsOpen && (
              <div className="absolute left-0 mt-2 w-64 rounded-lg border bg-white shadow-lg">
                <div className="py-1">
                  <NavLink
                    to="/tools"
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    All Tools
                  </NavLink>
                  <NavLink
                    to="/tools/quarterly-tax-deadlines"
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    Quarterly Tax Deadlines
                  </NavLink>
                  <NavLink
                    to="/tools/mileage-log"
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    Mileage Log
                  </NavLink>
                  <NavLink
                    to="/tools/state-tax-comparison"
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    State Tax Comparison
                  </NavLink>
                  <NavLink
                    to="/tools/break-even-calculator"
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    W-2 vs Freelance Calculator
                  </NavLink>
                </div>
              </div>
            )}
          </div>

          {/* Blog dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setResOpen(true)}
            onMouseLeave={() => setResOpen(false)}
            ref={resRef}
          >
            <NavLink to="/blog" className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-black">
              Blog
            </NavLink>
            {resOpen && (
              <div className="absolute left-0 mt-2 w-64 rounded-lg border bg-white shadow-lg">
                <div className="py-1">
                  <NavLink
                    to="/blog"
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    Guides & Posts
                  </NavLink>
                  <NavLink
                    to="/blog/external"
                    className={({ isActive }) =>
                      "block px-3 py-2 text-sm " +
                      (isActive ? "text-black bg-slate-50" : "text-slate-700 hover:bg-slate-50")
                    }
                  >
                    External Resources (authoritative)
                  </NavLink>
                </div>
              </div>
            )}
          </div>

          <NavLink to="/faq" className={linkCls}>FAQ</NavLink>
          <NavLink to="/trends" className={linkCls}>Job Trends</NavLink>
          <NavLink to="/contact" className={linkCls}>Contact</NavLink>
          
          {/* User Account or Login/CTA Buttons */}
          {currentUser ? (
            <div
              className="relative ml-4"
              ref={userRef}
            >
              <button 
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-primary-600 text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
              >
                <span className="w-7 h-7 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {currentUser.firstName?.charAt(0)?.toUpperCase()}{currentUser.lastName?.charAt(0)?.toUpperCase()}
                </span>
                <span className="hidden lg:inline">{currentUser.firstName || 'User'}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {userDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <p className="font-semibold text-gray-900">{currentUser.firstName || 'User'} {currentUser.lastName || ''}</p>
                    <p className="text-sm text-gray-500">{currentUser.email}</p>
                    <div className="mt-2">
                      {premiumStatus.isPremium ? (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">
                          ⭐ Premium
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-800">
                          Free Plan
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="py-2">
                    <Link
                      to="/tools/expense-tracker/app"
                      className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      My Portal
                    </Link>

                    {bankConnected && (
                      <button
                        onClick={handleDisconnectBank}
                        className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Disconnect Bank
                      </button>
                    )}

                    {!premiumStatus.isPremium && (
                      <a
                        href="/tools/expense-tracker/app"
                        className="block w-full px-4 py-2 text-left text-yellow-600 hover:bg-yellow-50 flex items-center gap-2 font-semibold"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                        Upgrade - $5.99/mo
                      </a>
                    )}

                    {(premiumStatus.isPremium || premiumStatus.stripeCustomerId) && (
                      <button
                        onClick={() => {
                          const customerId = premiumStatus.stripeCustomerId;
                          if (customerId) {
                            openCustomerPortal(customerId);
                          } else {
                            alert('Unable to access billing portal. Please contact support.');
                          }
                          setUserDropdownOpen(false);
                        }}
                        className="block w-full px-4 py-2 text-left text-blue-600 hover:bg-blue-50 flex items-center gap-2 font-semibold"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        💳 Manage Billing
                      </button>
                    )}
                  </div>

                  <div className="border-t border-gray-200 py-2">
                    <button
                      onClick={handleLogout}
                      className="block w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* CTA Button */}
              <Link
                to="/tools/expense-tracker"
                className="ml-4 px-4 py-2 bg-gradient-to-r from-primary-600 to-blue-600 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-primary-700 hover:to-blue-700 transition-all flex items-center gap-2"
              >
                <span className="text-base">💰</span>
                <span>Track Expenses</span>
              </Link>
              
              {/* Login Button */}
              <Link
                to="/tools/expense-tracker/login"
                className="ml-2 px-4 py-2 border-2 border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Login
              </Link>
            </>
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen(v => !v)}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {open ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </>
            ) : (
              <>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t bg-white">
          <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2 flex flex-col">
            {/* Calculators */}
            <details>
              <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:text-black">
                <span>Calculators</span>
              </summary>
              <div className="pl-3">
                <NavLink to="/" end className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  Tax Calculator
                </NavLink>
                <NavLink to="/retirement" className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  Retirement Calculator
                </NavLink>
                <NavLink to="/calculators/inflation" className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  Inflation Calculator
                </NavLink>
              </div>
            </details>

            {/* Tools */}
            <details className="mt-1">
              <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:text-black">
                <span>Tools</span>
              </summary>
              <div className="pl-3">
                <NavLink to="/tools" className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  All Tools
                </NavLink>
                <NavLink to="/tools/quarterly-tax-deadlines" className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  Quarterly Tax Deadlines
                </NavLink>
                <NavLink to="/tools/mileage-log" className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  Mileage Log
                </NavLink>
                <NavLink to="/tools/state-tax-comparison" className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  State Tax Comparison
                </NavLink>
                <NavLink to="/tools/break-even-calculator" className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  W-2 vs Freelance Calculator
                </NavLink>
              </div>
            </details>

            {/* Blog */}
            <details className="mt-1">
              <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:text-black">
                <span>Blog</span>
              </summary>
              <div className="pl-3">
                <NavLink to="/blog" className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  Guides & Posts
                </NavLink>
                <NavLink to="/blog/external" className="block px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
                  External Resources (authoritative)
                </NavLink>
              </div>
            </details>

            <NavLink to="/faq" className="px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
              FAQ
            </NavLink>
            <NavLink to="/trends" className="px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
              Job Trends
            </NavLink>
            <NavLink to="/contact" className="px-3 py-2 text-sm text-slate-700 hover:text-black" onClick={() => setOpen(false)}>
              Contact
            </NavLink>
            
            {/* Mobile User Account or CTA/Login */}
            {currentUser ? (
              <>
                <div className="mt-3 mx-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {currentUser.firstName?.charAt(0)?.toUpperCase()}{currentUser.lastName?.charAt(0)?.toUpperCase()}
                    </span>
                    <div>
                      <p className="font-semibold text-gray-900">{currentUser.firstName || 'User'} {currentUser.lastName || ''}</p>
                      <p className="text-xs text-gray-500">{currentUser.email}</p>
                    </div>
                  </div>
                  {premiumStatus.isPremium ? (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 mb-3">
                      ⭐ Premium
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-800 mb-3">
                      Free Plan
                    </span>
                  )}
                  <Link
                    to="/tools/expense-tracker/app"
                    className="block w-full px-4 py-2 bg-primary-600 text-white text-sm font-semibold rounded-lg text-center hover:bg-primary-700 transition-colors mb-2"
                    onClick={() => setOpen(false)}
                  >
                    Go to My Portal
                  </Link>

                  {bankConnected && (
                    <button
                      onClick={() => {
                        handleDisconnectBank();
                        setOpen(false);
                      }}
                      className="block w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg text-center hover:bg-gray-50 transition-colors mb-2"
                    >
                      Disconnect Bank
                    </button>
                  )}

                  {!premiumStatus.isPremium && (
                    <a
                      href="/tools/expense-tracker/app"
                      className="block w-full px-4 py-2 bg-yellow-50 border border-yellow-300 text-yellow-700 text-sm font-semibold rounded-lg text-center hover:bg-yellow-100 transition-colors mb-2"
                      onClick={() => setOpen(false)}
                    >
                      Upgrade - $5.99/mo
                    </a>
                  )}

                  {(premiumStatus.isPremium || premiumStatus.stripeCustomerId) && (
                    <button
                      onClick={() => {
                        const customerId = premiumStatus.stripeCustomerId;
                        if (customerId) {
                          openCustomerPortal(customerId);
                        } else {
                          alert('Unable to access billing portal. Please contact support.');
                        }
                        setOpen(false);
                      }}
                      className="block w-full px-4 py-2 bg-blue-50 border border-blue-300 text-blue-700 text-sm font-semibold rounded-lg text-center hover:bg-blue-100 transition-colors mb-2"
                    >
                      💳 Manage Billing
                    </button>
                  )}

                  <button
                    onClick={() => {
                      handleLogout();
                      setOpen(false);
                    }}
                    className="block w-full px-4 py-2 bg-white border border-red-300 text-red-600 text-sm font-semibold rounded-lg text-center hover:bg-red-50 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Mobile CTA */}
                <Link
                  to="/tools/expense-tracker"
                  className="mt-3 mx-3 px-4 py-3 bg-gradient-to-r from-primary-600 to-blue-600 text-white text-sm font-semibold rounded-lg shadow-md text-center flex items-center justify-center gap-2"
                  onClick={() => setOpen(false)}
                >
                  <span className="text-base">💰</span>
                  <span>Track Expenses - Try Free</span>
                </Link>
                
                {/* Mobile Login */}
                <Link
                  to="/tools/expense-tracker/login"
                  className="mt-3 mx-3 px-4 py-2 border-2 border-gray-300 text-gray-700 text-sm font-semibold rounded-lg text-center"
                  onClick={() => setOpen(false)}
                >
                  Login
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
